import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  styled,
  FormControlLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import { LoadingButton, TabContext } from '@mui/lab';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// component
import { api, mainurl } from 'src/Api';
import Alert from '@mui/material/Alert';
import { Encryption } from 'src/functions/function';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { Check } from '@mui/icons-material';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import VideoLabelIcon from '@mui/icons-material/VideoLabel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import OtpInput from 'react-otp-input';
import { useTheme } from '@emotion/react';
import GooglePlaces from 'src/components/GooglePlaces';
import palette from 'src/theme/palette';

// ----------------------------------------------------------------------

export default function RegisterForm({ role }) {
  const themePack = useTheme();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rolesData, setRolesData] = useState([]);
  const [showOtp, setShowOtp] = useState(false);

  // Add state for modal and subscription
  const [loading, setLoading] = useState(false);
  const [alert, setalert] = useState({ visible: false, message: '', color: 'success' });
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [otp, setOtp] = useState('');
  const [resendotp, setresendotp] = useState(false);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(60);
  const [userDetails, setUserDetails] = useState('');
  const [userToken, setUserToken] = useState('');
  const [billingPlans, setBillingPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState();
  const [selectedPriceId, setSelectedPriceId] = useState(0);
  const [isSubmittingCompany, setisSubmittingCompany] = useState(false);

  useEffect(() => {
    if (showOtp) {
      const interval = setInterval(() => {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        }
        if (seconds === 0) {
          if (minutes === 0) {
            clearInterval(interval);
          } else {
            setMinutes(minutes - 1);
            setSeconds(59);
          }
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [minutes, seconds, showOtp]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate('/login', { replace: true });
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    if (activeStep === 1) {
      setalert({ visible: false, message: '', color: 'success' });
    }
  }, [activeStep]);

  useEffect(() => {
    getBillingPlans();
  }, [selectedPlanId]);

  const getBillingPlans = () => {
    setLoading(true);
    const requestOptions = {
      method: 'GET',
      redirect: 'follow'
    };

    fetch(`${api.get_packages}?role_id=${selectedPlanId}`, requestOptions)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          return response.json();
        } else {
          return response.json().then((error) => {});
        }
      })
      .then((result) => {
        setBillingPlans(result.data);
      })
      .catch((error) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  // Validation schema with conditional company field
  const RegisterSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters long')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required')
  });

  const ComRegisterSchema = Yup.object().shape({
    role: Yup.string().required('Role is required'),
    companyName: Yup.string().required('Company Name is required'),
    address: Yup.object().shape({
      address: Yup.string().required('Address is required'),
      streetnumber: Yup.string().nullable(),
      streetname: Yup.string().nullable(),
      suburb: Yup.string().nullable(),
      state: Yup.string().nullable(),
      postalcode: Yup.string().nullable(),
      city: Yup.string().nullable(),
      zone: Yup.string().nullable(),
      country: Yup.string().nullable(),
      lat: Yup.string().required('Latitude is required'),
      lon: Yup.string().required('Longitude is required')
    }),
    phone: Yup.string().required('Business Phone is required'),
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    userPhone: Yup.string().required('Phone Number is required')
  });

  // Formik form
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: ''
    },
    validationSchema: RegisterSchema,
    onSubmit: () => {
      Register();
    }
  });

  const ComFormik = useFormik({
    initialValues: {
      role: '',
      companyName: '',
      address: {
        address: '',
        streetnumber: '',
        streetname: '',
        suburb: '',
        state: '',
        postalcode: '',
        city: '',
        zone: '',
        country: '',
        lat: '',
        lon: ''
      },
      phone: '',
      firstName: '',
      lastName: '',
      userPhone: ''
    },
    validationSchema: ComRegisterSchema,
    onSubmit: (values) => {
      UpdateCompanyInfo();
    }
  });

  const { errors, values, touched, handleSubmit, getFieldProps, setFieldValue } = formik;

  const {
    errors: comErr,
    touched: comTouched,
    values: comValues,
    getFieldProps: comGetFieldProps,
    handleSubmit: comHandleSubmit,
    setFieldValue: comSetFeildValue
  } = ComFormik;

  const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 13
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: theme.palette.success.main
      }
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: theme.palette.success.main
      }
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: 2,
      border: 0,
      backgroundColor: '#eaeaf0',
      borderRadius: 1,
      ...theme.applyStyles('dark', {
        backgroundColor: theme.palette.grey[800]
      })
    }
  }));

  const ColorlibStepIconRoot = styled('div')(({ theme }) => ({
    backgroundColor: '#ccc',
    zIndex: 1,
    color: '#fff',
    width: 30,
    height: 30,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[700]
    }),
    variants: [
      {
        props: ({ ownerState }) => ownerState.active,
        style: {
          backgroundColor: '#010f58',
          boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)'
        }
      },
      {
        props: ({ ownerState }) => ownerState.completed,
        style: {
          backgroundColor: theme.palette.success.main
        }
      }
    ]
  }));

  useEffect(() => {
    getRoles();
  }, []);

  function Register() {
    setIsSubmitting(true);

    const formData = new FormData();
    // formData.append('name', Encryption(values?.name, process.env.REACT_APP_SECRET_KEY));
    formData.append('email', Encryption(values?.email, process.env.REACT_APP_SECRET_KEY));
    formData.append('password', Encryption(values?.password, process.env.REACT_APP_SECRET_KEY));
    formData.append('role_id', Encryption(values?.role, process.env.REACT_APP_SECRET_KEY));
    formData.append(
      'password_confirmation',
      Encryption(values?.confirmPassword, process.env.REACT_APP_SECRET_KEY)
    );

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
    };

    fetch(api.sign_up_with_email_password, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          setShowOtp(true);
          setUserToken(result.access_token);
          setalert({ visible: true, message: result.message, color: 'success' });
          setIsSubmitting(false);
        } else {
          setIsSubmitting(false);
          setalert({ visible: true, message: result.error, color: 'error' });
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        setalert({ visible: true, message: 'An error occurred.', color: 'error' });
      });
  }

  const getRoles = () => {
    setLoading(true);
    const requestOptions = {
      method: 'GET',
      redirect: 'follow'
    };

    fetch(api.getRoleSignUp, requestOptions)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          return response.json();
        } else {
          return response.json().then((error) => {});
        }
      })
      .then((result) => {
        setRolesData(result.data);
      })
      .catch((error) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const otpInputStyle = {
    width: '100%',
    height: '56px',
    textAlign: 'center',
    fontSize: '18px',
    borderRadius: '4px',
    boxSizing: 'border-box',
    padding: '6px',
    border: `1px solid ${themePack.palette.primary.main} !important`
  };

  const otpContainerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 20,
    marginTop: 20
  };

  const steps = ['Select master blaster campaign settings', 'Create an ad group', 'Create an ad'];

  function ColorlibStepIcon(props) {
    const { active, completed, className } = props;

    const icons = {
      1: <PersonAddIcon style={{ height: 15, width: 15 }} />,
      2: <GroupAddIcon style={{ height: 15, width: 15 }} />,
      3: <VideoLabelIcon style={{ height: 15, width: 15 }} />
    };

    return (
      <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
        {completed ? <Check style={{ height: 15, width: 15 }} /> : <>{icons[String(props.icon)]}</>}
      </ColorlibStepIconRoot>
    );
  }

  const handleCode = () => {
    setLoadingVerify(true);

    if (seconds == 0) {
      // showToastError('OTP has been expired. Please request a new one.');
      setalert({
        visible: true,
        message: 'OTP has been expired. Please request a new one',
        color: 'error'
      });
      setLoadingVerify(false);
    } else {
      VerifyCode();
    }
  };

  function VerifyCode() {
    var myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: JSON.stringify({
        email: values.email,
        otp: otp
      }),
      redirect: 'follow'
    };
    setLoadingVerify(true);

    fetch(api.otp_verify, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          setalert({ visible: true, message: result.message });
          setUserDetails(result.data);
          handleNext();
          setLoadingVerify(false);
          setShowOtp(false);
        } else {
          setLoadingVerify(false);
          setalert({ visible: true, message: result.error, color: 'error' });
        }
      })
      .catch((error) => {
        setLoadingVerify(false);
        setalert({ visible: true, message: 'An error occurred.', color: 'error' });
      });
  }

  const handleResend = () => {
    setMinutes(0);
    setSeconds(60);
  };

  const ResendOTP = () => {
    setresendotp(true);
    var formdata = new FormData();
    formdata.append('email', values.email);
    formdata.append('type', 'register');

    var requestOptions = {
      method: 'POST',
      body: formdata
    };

    fetch(api.resend_otp, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          handleResend();
          setalert({ visible: true, message: result.message, color: 'success' });
        } else {
          setalert({ visible: true, message: result.error, color: 'error' });
        }
      })
      .catch((error) => {
        setalert({ visible: true, message: 'An error occurred.', color: 'error' });
      })
      .finally(() => {
        setresendotp(false);
      });
  };

  const formatTime = (time) => {
    return time < 10 ? `0${time}` : `${time}`;
  };

  function UpdateCompanyInfo() {
    setisSubmittingCompany(true);

    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${userToken}`);

    const formData = new FormData();
    formData.append('role_id', Encryption(comValues?.role, process.env.REACT_APP_SECRET_KEY));
    formData.append(
      'company',
      Encryption(comValues?.companyName, process.env.REACT_APP_SECRET_KEY)
    );
    formData.append(
      'location',
      Encryption(comValues?.address?.address, process.env.REACT_APP_SECRET_KEY)
    );
    formData.append(
      'country',
      Encryption(comValues?.address?.country, process.env.REACT_APP_SECRET_KEY)
    );
    formData.append('city', Encryption(comValues?.address?.city, process.env.REACT_APP_SECRET_KEY));
    formData.append('lat', Encryption(comValues?.address?.lat, process.env.REACT_APP_SECRET_KEY));
    formData.append('lon', Encryption(comValues?.address?.lon, process.env.REACT_APP_SECRET_KEY));
    formData.append(
      'business_phone_no',
      Encryption(comValues?.phone, process.env.REACT_APP_SECRET_KEY)
    );
    formData.append(
      'first_name',
      Encryption(comValues?.firstName, process.env.REACT_APP_SECRET_KEY)
    );
    formData.append('last_name', Encryption(comValues?.lastName, process.env.REACT_APP_SECRET_KEY));
    formData.append(
      'contact_no',
      Encryption(comValues?.userPhone, process.env.REACT_APP_SECRET_KEY)
    );

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(api.update_company_info, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          setalert({ visible: true, message: result.message, color: 'success' });
          handleNext();
          setSelectedPlanId(comValues.role);
        } else {
          setisSubmittingCompany(false);
        }
      })
      .catch((error) => {
        console.log('error', error);
        setisSubmittingCompany(false);
      });
  }

  // Function to handle the checkout process
  const handleCheckout = async (price_id) => {
    setLoadingSubs(true);
    try {
      const response = await fetch(`${mainurl}v1/subscription/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(selectedPriceId === 0 && { is_trail: 1 }), // Only add is_trail when selectedPriceId is 0
          lookup_key: price_id,
          user_id: userDetails?.id,
          is_signup: 1
        })
      });

      const data = await response.json();
      setLoadingSubs(false);

      if (data?.url) {
        window.location.href = data?.url;
      } else {
        console.log('data', data);
        setalert({ visible: true, message: 'Profile Completed Successfully.', color: 'success' });
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.data));
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      }
    } catch (error) {
      setLoadingSubs(false);
      console.error('Error creating checkout session:', error);
      // setMessage('An error occurred while processing your request.');
    }
  };

  const handleRadioChange = (event) => {
    setSelectedPriceId(event.target.value); // Set the selected price_id
  };

  const handleRadioChangeFree = () => {
    setSelectedPriceId(0); // Set the selected price_id
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> */}
      {/* <IconButton onClick={handleBack} style={{ color: '#010f58', marginBottom: 12 }}>
          <Iconify icon={'ion:arrow-back'} />
        </IconButton> */}
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        connector={<ColorlibConnector />}
        sx={{ marginBottom: 2, flex: 1 }}
      >
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel StepIconComponent={ColorlibStepIcon}></StepLabel>
          </Step>
        ))}
      </Stepper>
      {/* </div> */}
      <div style={{ flex: 1 }}>
        {activeStep === 0 && (
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <TextField
                fullWidth
                disabled={showOtp}
                type="email"
                label="Email Address"
                sx={{ mb: 2, mt: 2 }}
                {...getFieldProps('email')}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
              />

              <TextField
                fullWidth
                disabled={showOtp}
                autoComplete="current-password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                {...getFieldProps('password')}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                        <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password}
              />

              <TextField
                fullWidth
                disabled={showOtp}
                autoComplete="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                label="Confirm Password"
                {...getFieldProps('confirmPassword')}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                      >
                        <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                helperText={touched.confirmPassword && errors.confirmPassword}
              />

              {showOtp && (
                <>
                  <div style={otpContainerStyle}>
                    <OtpInput
                      value={otp}
                      onChange={setOtp}
                      numInputs={6}
                      renderSeparator={<span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>}
                      renderInput={(props) => <input {...props} style={otpInputStyle} />}
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginBottom: 10
                    }}
                  >
                    {resendotp ? (
                      <p style={{ fontSize: 13, color: palette.primary.main }}>loading...</p>
                    ) : (
                      <p
                        style={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontWeight: 'bold',
                          fontSize: 13
                        }}
                        disabled={seconds == 0 ? false : true}
                        onClick={() => {
                          if (seconds == 0) {
                            ResendOTP();
                          }
                        }}
                      >
                        {seconds > 0 ? (
                          `\t ${formatTime(minutes)}:${formatTime(seconds)}`
                        ) : (
                          <>Resend OTP</>
                        )}{' '}
                      </p>
                    )}
                  </div>

                  {alert.visible && (
                    <Alert
                      onClose={() => {
                        setalert({ visible: false, message: '', color: 'success' });
                      }}
                      severity={alert?.color}
                      style={{ marginBottom: 10 }}
                    >
                      {alert.message}
                    </Alert>
                  )}

                  <LoadingButton
                    onClick={handleCode}
                    fullWidth
                    size="large"
                    variant="contained"
                    loading={loadingVerify}
                  >
                    Verify
                  </LoadingButton>
                </>
              )}

              {!showOtp && (
                <>
                  {alert.visible && (
                    <Alert
                      onClose={() => {
                        setalert({ visible: false, message: '', color: 'success' });
                      }}
                      severity={alert?.color}
                      style={{ marginBottom: 10 }}
                    >
                      {alert.message}
                    </Alert>
                  )}
                  <LoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                  >
                    Sign up
                  </LoadingButton>
                </>
              )}
            </Form>
          </FormikProvider>
        )}
        {activeStep === 1 && (
          <FormikProvider value={ComFormik}>
            <Form autoComplete="off" onSubmit={comHandleSubmit}>
              <p
                style={{
                  fontSize: 15,
                  fontWeight: 'bold',
                  color: '#010f58',
                  textTransform: 'capitalize',
                  marginBottom: 15
                }}
              >
                your company information
              </p>

              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  value={comValues.role} // Access the current value from Formik
                  onChange={(e) => comSetFeildValue('role', e.target.value)} // Correctly update the value
                  error={Boolean(comTouched.role && comErr.role)}
                  sx={{ mb: 2 }}
                >
                  {rolesData.map((val) => (
                    <MenuItem key={val.id} value={val.id}>
                      {val.role_name}
                    </MenuItem>
                  ))}
                </Select>
                {comTouched?.role && comErr?.role && (
                  <div style={{ color: '#FF4842', fontSize: 12, marginLeft: 14, marginTop: 4 }}>
                    {comErr?.role}
                  </div>
                )}
              </FormControl>

              <TextField
                fullWidth
                sx={{ mb: 2 }}
                label="Company Name"
                {...comGetFieldProps('companyName')}
                error={Boolean(comTouched.companyName && comErr.companyName)}
                helperText={comTouched.companyName && comErr.companyName}
              />

              {/* <Grid item xs={12} md={6} mt={2}> */}
              <GooglePlaces
                width="100%"
                onResult={(val) => {
                  comSetFeildValue('address', val);
                }}
                required={true}
                placeholder={'Address'}
                // regions={true}
                address={comValues?.address?.address}
                height={55}
                fontSize={14}
                padding={'0 55px 0 10px'}
              />
              {/* </Grid> */}

              {comValues.address && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2}>
                  {comValues.address.city && (
                    <TextField
                      disabled={comValues.address.city}
                      fullWidth
                      value={comValues.address.city}
                      label="City"
                      // {...companyGetFieldProps('city')}
                      error={Boolean(comTouched.city && comErr.city)}
                      helperText={comTouched.city && comErr.city}
                    />
                  )}
                  {comValues.address.country && (
                    <TextField
                      disabled={comValues.address.country}
                      fullWidth
                      value={comValues.address.country}
                      label="Country"
                      // {...companyGetFieldProps('country')}
                      error={Boolean(comTouched.country && comErr.country)}
                      helperText={comTouched.country && comErr.country}
                    />
                  )}
                </Stack>
              )}

              <TextField
                fullWidth
                sx={{ mt: 2 }}
                type="number"
                label="Business Phone"
                {...comGetFieldProps('phone')}
                error={Boolean(comTouched.phone && comErr.phone)}
                helperText={comTouched.phone && comErr.phone}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow numeric characters
                  if (numericValue.length <= 20) {
                    comSetFeildValue('phone', e.target.value); // Correctly update the value
                    // setPhoneNumber(numericValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                    e.preventDefault(); // Prevent "e", "E", "+", and "-" from being typed
                  }
                }}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2}>
                <TextField
                  fullWidth
                  value={comValues.firstName}
                  label="First Name"
                  {...comGetFieldProps('firstName')}
                  error={Boolean(comTouched.firstName && comErr.firstName)}
                  helperText={comTouched.firstName && comErr.firstName}
                />
                <TextField
                  fullWidth
                  value={comValues.lastName}
                  label="Last Name"
                  {...comGetFieldProps('lastName')}
                  error={Boolean(comTouched.lastName && comErr.lastName)}
                  helperText={comTouched.lastName && comErr.lastName}
                />
              </Stack>

              <TextField
                fullWidth
                type="number"
                sx={{ mt: 2 }}
                label="Phone Number"
                {...comGetFieldProps('userPhone')}
                error={Boolean(comTouched.userPhone && comErr.userPhone)}
                helperText={comTouched.userPhone && comErr.userPhone}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow numeric characters
                  if (numericValue.length <= 20) {
                    comSetFeildValue('userPhone', e.target.value); // Correctly update the value
                    // setPhoneNumber(numericValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                    e.preventDefault(); // Prevent "e", "E", "+", and "-" from being typed
                  }
                }}
              />

              {alert.visible && <Alert severity={alert?.color}>{alert.message}</Alert>}

              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                // onClick={() => {companyHandleSubmit()}}
                variant="contained"
                loading={isSubmittingCompany}
                sx={{ mt: 2 }}
              >
                Save
              </LoadingButton>
            </Form>
          </FormikProvider>
        )}

        {activeStep === 2 && (
          <div>
            <p
              style={{
                fontSize: 15,
                fontWeight: 'bold',
                color: '#010f58',
                marginBottom: 15
              }}
            >
              Select your subscription plan
            </p>
            <RadioGroup
              value={selectedPriceId} // Bind selectedPriceId to RadioGroup
              onChange={handleRadioChangeFree} // Handle change event
            >
              <div
                style={{
                  border: '2px solid #010f58',
                  padding: 10,
                  borderRadius: 5,
                  marginBottom: 10,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {/* Checkbox for selecting the price_id */}
                <FormControlLabel
                  control={
                    <Radio
                      value={selectedPriceId} // Set value to price_id
                      checked={selectedPriceId === 0} // Check if this price_id is selected
                    />
                  }
                />
                <div style={{ flex: 1, marginLeft: -15 }}>
                  <p
                    style={{
                      fontSize: 15,
                      fontWeight: 'bold',
                      color: '#010f58'
                    }}
                  >
                    Free Trial
                  </p>
                </div>
              </div>
            </RadioGroup>

            <div>
              <RadioGroup
                value={selectedPriceId} // Bind selectedPriceId to RadioGroup
                onChange={handleRadioChange} // Handle change event
              >
                {billingPlans.map((plan) => (
                  <div
                    key={plan.price_id}
                    style={{
                      border: '2px solid #010f58',
                      padding: 10,
                      borderRadius: 5,
                      marginBottom: 10,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start'
                    }}
                  >
                    {/* Checkbox for selecting the price_id */}
                    <FormControlLabel
                      control={
                        <Radio
                          value={plan.price_id} // Set value to price_id
                          checked={selectedPriceId === plan.price_id} // Check if this price_id is selected
                        />
                      }
                    />
                    <div style={{ flex: 1, marginLeft: -15 }}>
                      <p
                        style={{
                          fontSize: 15,
                          fontWeight: 'bold',
                          color: '#010f58'
                        }}
                      >
                        {plan.duration}
                      </p>
                      <p
                        style={{
                          fontSize: 13,
                          fontWeight: 'bold',
                          color: '#010f58',
                          marginLeft: 10
                        }}
                      >
                        ${plan.amount}
                      </p>
                      {plan.package_details.map((detail) => (
                        <p
                          key={detail.description}
                          style={{
                            fontSize: 11,
                            color: '#444',
                            marginLeft: 12
                          }}
                        >
                          - {detail.description}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </RadioGroup>

              {alert.visible && (
                <Alert
                  onClose={() => {
                    setalert({ visible: false, message: '', color: 'success' });
                  }}
                  severity={alert?.color}
                  style={{ marginBottom: 10 }}
                >
                  {alert.message}
                </Alert>
              )}

              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                onClick={() => handleCheckout(selectedPriceId)}
                variant="contained"
                loading={loadingSubs}
                sx={{ mt: 2 }}
              >
                Save
              </LoadingButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

{
  /* */
}
